import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Star, Upload, X, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;
function resolveUrl(u) { return (!u || u.startsWith('http')) ? u : `${BASE}${u}`; }

function StarRating({ rating, setRating, readOnly = false, size = 18 }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" disabled={readOnly}
          onMouseEnter={() => !readOnly && setHover(n)}
          onMouseLeave={() => !readOnly && setHover(0)}
          onClick={() => !readOnly && setRating(n)}
          className={`${readOnly ? 'cursor-default' : 'cursor-pointer'} transition-colors`}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
        >
          <Star size={size} className={`${(hover || rating) >= n ? 'fill-[#C6A978] text-[#C6A978]' : 'text-[#DDD7CE]'}`} />
        </button>
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  const date = new Date(review.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  return (
    <div data-testid="review-card" className="bg-[#F6F3EE] border border-[#DDD7CE]/60 p-4 md:p-5">
      <div className="flex items-start gap-3 mb-2">
        <div className="w-9 h-9 bg-[#E8E1D8] flex items-center justify-center flex-shrink-0 rounded-full">
          <User size={14} className="text-[#7A7369]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-['Cormorant_Garamond'] text-base font-medium text-[#1E1C1A] leading-tight">{review.customer_name}</p>
          <div className="flex items-center gap-2 mt-1">
            <StarRating rating={review.rating} readOnly size={12} />
            <span className="text-[10px] text-[#7A7369] tracking-wide">{date}</span>
          </div>
        </div>
      </div>
      <p className="text-xs md:text-sm font-light text-[#3A3632] leading-relaxed mb-2 whitespace-pre-wrap">{review.comment}</p>
      {review.image_url && (
        <div className="w-20 h-20 md:w-24 md:h-24 overflow-hidden mt-2">
          <img src={resolveUrl(review.image_url)} alt="Customer review" loading="lazy"
            className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => window.open(resolveUrl(review.image_url), '_blank')} />
        </div>
      )}
    </div>
  );
}

function ReviewForm({ productId, onSuccess }) {
  const [name, setName] = useState('');
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Image too large (max 5MB)'); return; }
    setUploading(true); setError('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/reviews/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImageUrl(data.url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Try again.');
    } finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !comment.trim()) { setError('Please fill in your name and feedback'); return; }
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API}/reviews`, { product_id: productId || null, customer_name: name.trim(), rating, comment: comment.trim(), image_url: imageUrl });
      setName(''); setRating(5); setComment(''); setImageUrl('');
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit. Try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="review-form" className="bg-[#F6F3EE] border border-[#DDD7CE] p-4 md:p-6 space-y-4">
      <div>
        <label className="text-[11px] font-medium uppercase tracking-[0.15em] text-[#1E1C1A] mb-1.5 block">Your Name</label>
        <input type="text" data-testid="review-name-input" value={name} onChange={e => setName(e.target.value)} required maxLength={60}
          className="w-full bg-[#E8E1D8] border border-[#DDD7CE] text-[#1E1C1A] font-light px-3 py-2 text-sm focus:outline-none focus:border-[#C6A978]" placeholder="e.g. Priya S." />
      </div>
      <div>
        <label className="text-[11px] font-medium uppercase tracking-[0.15em] text-[#1E1C1A] mb-1.5 block">Rating</label>
        <StarRating rating={rating} setRating={setRating} size={22} />
      </div>
      <div>
        <label className="text-[11px] font-medium uppercase tracking-[0.15em] text-[#1E1C1A] mb-1.5 block">Your Feedback</label>
        <textarea data-testid="review-comment-input" value={comment} onChange={e => setComment(e.target.value)} required rows={4} maxLength={1000}
          className="w-full bg-[#E8E1D8] border border-[#DDD7CE] text-[#1E1C1A] font-light px-3 py-2 text-sm focus:outline-none focus:border-[#C6A978] resize-none"
          placeholder="Share your experience..." />
        <p className="text-[10px] text-[#7A7369] mt-1">{comment.length}/1000</p>
      </div>
      <div>
        <label className="text-[11px] font-medium uppercase tracking-[0.15em] text-[#1E1C1A] mb-1.5 block">Photo (Optional)</label>
        <div className="flex items-center gap-3">
          {imageUrl && (
            <div className="relative w-16 h-16 bg-[#E8E1D8] overflow-hidden">
              <img src={resolveUrl(imageUrl)} alt="Preview" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setImageUrl('')} className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 text-white flex items-center justify-center" aria-label="Remove image"><X size={10} /></button>
            </div>
          )}
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="review-image-upload"
            className="flex items-center gap-2 px-4 py-2 text-[11px] tracking-wide border border-[#DDD7CE] text-[#3A3632] hover:border-[#3A3632] transition-colors disabled:opacity-50">
            <Upload size={12} /> {uploading ? 'Uploading...' : imageUrl ? 'Change Photo' : 'Upload Photo'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImage} />
        </div>
        <p className="text-[10px] text-[#7A7369] mt-1.5">Max 5MB · JPG, PNG, or WEBP</p>
      </div>
      {error && <p data-testid="review-error" className="text-xs text-red-600">{error}</p>}
      <button type="submit" disabled={submitting || uploading} data-testid="review-submit-btn"
        className="w-full bg-[#1E1C1A] text-[#F6F3EE] py-3 text-[11px] tracking-[0.2em] uppercase hover:bg-[#3A3632] transition-colors disabled:opacity-50">
        {submitting ? 'Submitting...' : 'Submit Review'}
      </button>
    </form>
  );
}

export default function ReviewsSection({ productId = null, title = 'Customer Reviews', showForm = true, limit = null }) {
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false); // FIX: false until section is visible
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showFormPanel, setShowFormPanel] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [visibleCount, setVisibleCount] = useState(limit || 6);
  const sectionRef = useRef(null);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      if (productId) {
        const { data } = await axios.get(`${API}/reviews/product/${productId}`);
        setReviews(data.reviews || []);
        setAvgRating(data.average_rating || 0);
        setTotal(data.total || 0);
      } else {
        const { data } = await axios.get(`${API}/reviews?limit=20`);
        setReviews(data || []);
        setTotal((data || []).length);
        if (data?.length) setAvgRating(+(data.reduce((s, r) => s + r.rating, 0) / data.length).toFixed(1));
      }
    } catch (err) {
      console.error('Failed to fetch reviews', err);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  // FIX: Use IntersectionObserver to only fetch reviews when section scrolls into view
  // This removes reviews from the critical path — hero/products/collections load first
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasLoaded) {
        fetchReviews();
        observer.disconnect();
      }
    }, { rootMargin: '200px' }); // start loading 200px before visible
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, hasLoaded]);

  const handleSuccess = () => {
    setShowFormPanel(false);
    setSuccessMsg('Thank you! Your review has been posted.');
    fetchReviews();
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  return (
    <section ref={sectionRef} data-testid="reviews-section" className="py-10 md:py-16 px-4 md:px-12 lg:px-24 bg-[#E8E1D8]/40">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-6 md:mb-10">
          <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.3em] text-[#C6A978] mb-2">Loved by our customers</p>
          <h2 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl lg:text-3xl font-light tracking-tight text-[#1E1C1A] mb-3">{title}</h2>
          {total > 0 && (
            <div className="flex items-center justify-center gap-2">
              <StarRating rating={Math.round(avgRating)} readOnly size={16} />
              <span className="text-sm text-[#3A3632] font-light">{avgRating} ({total} review{total !== 1 ? 's' : ''})</span>
            </div>
          )}
        </div>

        {successMsg && (
          <div data-testid="review-success" className="mb-6 p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs text-center">
            {successMsg}
          </div>
        )}

        {showForm && (
          <div className="mb-8 md:mb-10 text-center">
            {!showFormPanel ? (
              <button onClick={() => setShowFormPanel(true)} data-testid="write-review-btn"
                className="inline-flex items-center gap-2 border border-[#1E1C1A] text-[#1E1C1A] hover:bg-[#1E1C1A] hover:text-[#F6F3EE] px-6 py-3 text-[10px] md:text-xs tracking-[0.2em] uppercase transition-all">
                Write a Review
              </button>
            ) : (
              <div className="max-w-xl mx-auto text-left">
                <ReviewForm productId={productId} onSuccess={handleSuccess} />
                <button onClick={() => setShowFormPanel(false)} className="mt-3 text-[10px] tracking-[0.15em] uppercase text-[#7A7369] hover:text-[#1E1C1A] transition-colors">Cancel</button>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center py-10">
            <div className="w-6 h-6 border border-[#C6A978] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : !hasLoaded ? (
          // Placeholder height so layout doesn't jump when reviews load
          <div className="py-10" />
        ) : reviews.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm font-light text-[#7A7369]">No reviews yet. Be the first to share your experience!</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {reviews.slice(0, visibleCount).map(r => <ReviewCard key={r.id} review={r} />)}
            </div>
            {reviews.length > visibleCount && (
              <div className="text-center mt-6">
                <button onClick={() => setVisibleCount(c => c + 6)} data-testid="load-more-reviews"
                  className="text-[10px] md:text-xs font-medium uppercase tracking-[0.2em] text-[#1E1C1A] border-b border-[#1E1C1A] pb-0.5 hover:text-[#C6A978] hover:border-[#C6A978] transition-colors">
                  Load More Reviews
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}